import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A passenger's name as the console shows it — never blank (#873).
 *
 * The roster's `fullName` is already masked. A passenger a loose PNR brought in has
 * no name on file (that door asks for the party size, not names —
 * `routes/cases/manual-pnr-list.tsx`), so it reads «Pasajero N» instead of an empty
 * row: N is its 1-based place in its PNR, the same number on every surface.
 */
export function paxDisplayName(
  fullName: string | null | undefined,
  index: number,
  unnamed: (n: number) => string,
): string {
  const name = fullName?.trim();
  return name ? name : unnamed(index + 1);
}

/**
 * A revealed name, or `undefined` when the reveal has none to give. The reveal
 * answers a passenger with no name on file with an honest placeholder in
 * parentheses — "(no name on file)", English (operator-bff `revealValue`) — which
 * is never a name: the caller keeps its {@link paxDisplayName} fallback.
 */
export function revealedName(value: string | null | undefined): string | undefined {
  const name = value?.trim();
  return name && !/^\(.*\)$/.test(name) ? name : undefined;
}

/** {@link paxDisplayName} with the console's «Pasajero N» copy. */
export function usePaxDisplayName(): (fullName: string | null | undefined, index: number) => string {
  const { t } = useTranslation('pnr');
  return useCallback(
    (fullName, index) => paxDisplayName(fullName, index, (n) => t('pax.unnamed', { n })),
    [t],
  );
}
